import { useEffect, useRef, useState } from 'react';
import type { DemoScript, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BoardFrame, OK, STROKE, alpha, clamp, easier, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { SCENES_BY_ERA, ScenePicture } from './scenes.js';
import type { SceneId } from './scenes.js';
import { ease, shuffle } from './things.js';

/**
 * ARCHIVIST · MEND THE PICTURE — tap the tiles to turn them until the picture is whole.
 *
 * A torn poster in the city, an old photo in the garden: a drawn picture cut into square tiles,
 * some of them turned a quarter, a half or three quarters. The whole picture shows first; then it
 * breaks into tiles and a small copy of it stays at the left of the board to go by. Each tap
 * turns a tile a quarter to the right; turning costs nothing, so there are no mistakes. Round 1
 * is 2 × 2 with three tiles turned, round 2 is 2 × 2 with all four turned (both count the tiles
 * the right way up), round 3 is 3 × 3 with five turned and no count.
 *
 * On the kit (kit.tsx): the picture and its tiles are drawn in the board's own 320 × 320 space;
 * the view state (`MendView`) holds each tile's turn as it stands, gliding while a tile turns.
 */

export interface MendLevel {
  cols: number;
  /** How many tiles are turned. */
  turned: number;
  /** Show how many tiles are the right way up. */
  count: boolean;
  /** How long the whole picture shows first. */
  showMs: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const MEND_LEVELS: readonly MendLevel[] = easier(
  [
    { cols: 2, turned: 3, count: true, showMs: 1_500 },
    { cols: 3, turned: 5, count: false, showMs: 1_400 },
  ],
);

/** Each tile's quarter turns: `turned` tiles get 1 to 3, the rest stay put. */
export function scrambleTurns(level: Level, rand: () => number): number[] {
  const { cols, turned } = MEND_LEVELS[level]!;
  const n = cols * cols;
  const which = new Set(shuffle(Array.from({ length: n }, (_, i) => i), rand).slice(0, turned));
  return Array.from({ length: n }, (_, i) => (which.has(i) ? 1 + Math.floor(rand() * 3) : 0));
}

/** Every tile the right way up. */
export const isWhole = (turns: readonly number[]) => turns.every((t) => t % 4 === 0);

/** How many taps it takes, at best. */
export const tapsToMend = (turns: readonly number[]) => turns.reduce((sum, t) => sum + ((4 - (t % 4)) % 4), 0);

/** The pictures shown lately, so the three rounds show three different ones. */
const recent: SceneId[] = [];

/** A picture for the era, not one of those in `avoid` while there are others. */
export function chooseScene(era: Era, rand: () => number, avoid: readonly SceneId[] = []): SceneId {
  const all = SCENES_BY_ERA[era];
  const fresh = all.filter((s) => !avoid.includes(s));
  const from = fresh.length ? fresh : all;
  return from[Math.floor(rand() * from.length)]!;
}

/** A tile turns a quarter in this long. */
const TURN_MS = 300;
/** The tiles part, and close again once the picture is whole, over this long. */
const GAP_MS = 350;

/** The small copy to go by, at the left; the picture in its frame, to the right of it (board units). */
const THUMB = { x: 8, y: 36, s: 50 } as const;
const FRAME = { x: 64, y: 35, s: 250, pad: 6 } as const;
/** The tiles' gap when they stand apart. */
const GAP = 3;

/** Where tile `i` of a `cols` × `cols` picture lies, with the tiles `g` (0–1) apart. */
export function tileBox(i: number, cols: number, g = 1): { x: number; y: number; s: number } {
  const area = FRAME.s - 2 * FRAME.pad;
  const gap = GAP * g;
  const s = (area - (cols - 1) * gap) / cols;
  return { x: FRAME.x + FRAME.pad + (i % cols) * (s + gap), y: FRAME.y + FRAME.pad + Math.floor(i / cols) * (s + gap), s };
}

/** The tile under a finger at `p`, or −1. */
export function tileAt(p: Pt, cols: number): number {
  const area = FRAME.s - 2 * FRAME.pad;
  const col = Math.floor(((p.x - FRAME.x - FRAME.pad) / area) * cols);
  const row = Math.floor(((p.y - FRAME.y - FRAME.pad) / area) * cols);
  return col >= 0 && col < cols && row >= 0 && row < cols ? row * cols + col : -1;
}

/** A tile's turn, read the right way: 0 when it is right way up, 1–3 when it is not. */
const quarter = (r: number) => (((Math.round(r) % 4) + 4) % 4);

/** The tiles in drawing order: those at rest first, those on the turn after them. */
const order = (r: readonly number[]) => r.map((_, i) => i).sort((a, b) => Number(!Number.isInteger(r[a])) - Number(!Number.isInteger(r[b])));

/** Everything the picture shows. */
export interface MendView {
  /** The game clock, ms. */
  t: number;
  /** The picture. */
  p: SceneId;
  /** Each tile's turn in quarters, to the right (it glides while a tile turns; a whole multiple of 4 is right way up). */
  r: readonly number[];
  /** 0 the whole picture shows, 1 mending, 2 whole again. */
  s: 0 | 1 | 2;
  /** How far the tiles stand apart, 0–1. */
  g: number;
  /** The status counts the tiles the right way up (rounds 1 and 2). */
  n: boolean;
  fx?: Fx | undefined;
}

/** The picture, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<MendView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const photo = era === 'canopy' || era === 'stars';
  const cols = Math.max(1, Math.round(Math.sqrt(view.r.length)));
  const g = clamp(view.g, 0, 1);
  const cut = 120 / cols;
  const right = view.r.filter((r) => quarter(r) === 0).length;
  const hint = say(lang, 'TAP TO TURN', 'TRYKK FOR Å SNU');
  const status =
    view.s === 0
      ? { text: say(lang, 'THE PICTURE', 'BILDET'), hint: say(lang, 'LOOK CLOSELY', 'SE GODT PÅ DET') }
      : view.s === 2
        ? { text: say(lang, '✓ WHOLE AGAIN', '✓ HELT IGJEN'), tone: 'ok' as const }
        : view.n
          ? { text: say(lang, 'RIGHT WAY UP', 'RIKTIG VEI'), count: [right, view.r.length] as const, hint }
          : { text: say(lang, 'MEND THE PICTURE', 'REPARER BILDET'), hint };
  const mount = photo ? '#f4efe3' : `url(#${id}mount)`;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}mount`} x1="0" y1="0" x2=".4" y2="1">
          <stop offset="0" stopColor={alpha(skin.ink, 0.4)} />
          <stop offset="1" stopColor={skin.bg} />
        </linearGradient>
      </defs>

      {/* the small copy to go by, once the picture has broken into tiles */}
      {view.s > 0 && (
        <g>
          <rect x={THUMB.x} y={THUMB.y} width={THUMB.s} height={THUMB.s} rx="4" fill={mount} stroke={alpha(accent, 0.45)} />
          <ScenePicture scene={view.p} x={THUMB.x + 3} y={THUMB.y + 3} width={THUMB.s - 6} height={THUMB.s - 6} />
        </g>
      )}

      {/* the frame, glowing once the picture is whole */}
      <rect x={FRAME.x + 2} y={FRAME.y + 6} width={FRAME.s} height={FRAME.s} rx="6" fill="#000000" opacity=".45" />
      {view.s === 2 && <rect x={FRAME.x - 3} y={FRAME.y - 3} width={FRAME.s + 6} height={FRAME.s + 6} rx="9" fill="none" stroke={OK} strokeWidth="6" opacity=".35" />}
      <rect x={FRAME.x} y={FRAME.y} width={FRAME.s} height={FRAME.s} rx="6" fill={mount} stroke={view.s === 2 ? OK : 'none'} strokeWidth={STROKE.line} />

      {/* a tile on the turn is drawn last, so it passes over its neighbours */}
      {order(view.r).map((i) => {
        const r = view.r[i]!;
        const b = tileBox(i, cols, g);
        const cx = b.x + b.s / 2;
        const cy = b.y + b.s / 2;
        const sx = (i % cols) * cut;
        const sy = Math.floor(i / cols) * cut;
        return (
          <g key={i}>
            {g > 0 && <rect x={b.x + 1} y={b.y + 2.5} width={b.s} height={b.s} rx={3 * g} fill="#000000" opacity={0.55 * g} />}
            <g transform={`rotate(${q(r * 90, 1)} ${q(cx, 1)} ${q(cy, 1)})`}>
              <clipPath id={`${id}t${i}`}>
                <rect x={b.x} y={b.y} width={b.s} height={b.s} rx={3 * g} />
              </clipPath>
              <g clipPath={`url(#${id}t${i})`}>
                <ScenePicture scene={view.p} view={`${sx} ${sy} ${cut} ${cut}`} x={q(b.x, 2)} y={q(b.y, 2)} width={q(b.s, 2)} height={q(b.s, 2)} />
              </g>
            </g>
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, era, lang } = props;
  const [setup] = useState(() => {
    const rand = seeded(Date.now() + level * 7919);
    return { scene: chooseScene(era, rand, recent.slice(0, 2)), target: scrambleTurns(level, rand) };
  });
  useEffect(() => {
    if (recent.includes(setup.scene)) recent.splice(recent.indexOf(setup.scene), 1);
    recent.unshift(setup.scene);
    recent.length = Math.min(recent.length, 4);
  }, [setup.scene]);
  const { cols, count, showMs } = MEND_LEVELS[level]!;
  const scrambled = setup.target.filter((t) => t % 4 !== 0).length;
  /** Each tile turning from one turn to the next (in quarters), since when (game clock). */
  const tiles = useRef(setup.target.map(() => ({ from: 0, to: 0, at: -1e9 })));
  const startedAt = useRef<number | null>(null);
  /** When the whole picture broke into turned tiles, and when it was whole again. */
  const playAt = useRef<number | null>(null);
  const wholeAt = useRef<number | null>(null);

  const round = useRound(props, (_dt, now) => {
    if (startedAt.current === null) startedAt.current = now;
    if (playAt.current === null && now >= startedAt.current + showMs) {
      playAt.current = now;
      tiles.current = setup.target.map((to) => ({ from: 0, to, at: now }));
    }
  });

  const tap = (p: Pt) => {
    if (!round.live || playAt.current === null) return;
    const i = tileAt(p, cols);
    if (i < 0) return;
    const now = round.time();
    const base = tiles.current[i]!.to % 4;
    tiles.current[i] = { from: base, to: base + 1, at: now };
    const turns = tiles.current.map((t) => t.to);
    const right = turns.filter((t, j) => t % 4 === 0 && setup.target[j]! % 4 !== 0).length;
    round.progress(scrambled ? right / scrambled : 1);
    if ((base + 1) % 4 === 0) round.hit();
    if (isWhole(turns)) {
      wholeAt.current = now;
      round.win();
    }
  };

  const now = round.now;
  const play = playAt.current;
  const view: MendView = {
    t: q(now),
    p: setup.scene,
    r: tiles.current.map((t) => q(t.from + (t.to - t.from) * ease((now - t.at) / TURN_MS), 2)),
    s: wholeAt.current !== null ? 2 : play !== null ? 1 : 0,
    g:
      wholeAt.current !== null
        ? q(1 - clamp((now - wholeAt.current) / GAP_MS, 0, 1), 2)
        : play !== null
          ? q(clamp((now - play) / GAP_MS, 0, 1), 2)
          : 0,
    n: count,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap a tile to turn it', 'Trykk på en bit for å snu den'), down: tap }}
    />
  );
}

/** The middle of tile `i` in a 2 × 2 picture, with the tiles apart: where the finger taps. */
const tileMid = (i: number) => {
  const b = tileBox(i, 2);
  return { x: q(b.x + b.s / 2), y: q(b.y + b.s / 2) };
};
const DEMO_A = tileMid(1);
const DEMO_B = tileMid(2);

/**
 * The how-to, on round 1's 2 × 2: the whole lighthouse, then it breaks and two tiles turn askew;
 * the finger taps the top right one once and the bottom left one twice, and the picture is whole.
 */
const demo: DemoScript<MendView> = {
  ms: 7_000,
  keys: [
    { at: 0, view: { t: 0, p: 'lighthouse', r: [0, 0, 0, 0], s: 0, g: 0, n: true } },
    { at: 1_300, view: { s: 1 }, ease: 'step' },
    { at: 1_300 + GAP_MS, view: { r: [0, 3, 2, 0], g: 1 } },
    { at: 2_400, view: { r: [0, 3, 2, 0] } },
    { at: 2_400 + TURN_MS, view: { r: [0, 4, 2, 0], fx: [0.8, 0] } },
    { at: 3_100, view: { fx: [0, 0] } },
    { at: 3_400, view: { r: [0, 4, 2, 0] } },
    { at: 3_400 + TURN_MS, view: { r: [0, 4, 3, 0] } },
    { at: 4_100, view: { r: [0, 4, 3, 0] } },
    { at: 4_100 + TURN_MS, view: { r: [0, 4, 4, 0] } },
    { at: 4_420, view: { s: 2, fx: [1, 0] }, ease: 'step' },
    { at: 4_420 + GAP_MS, view: { g: 0 } },
    { at: 5_100, view: { fx: [0, 0] } },
  ],
  finger: [
    { at: 0, x: 270, y: 300, hide: true },
    { at: 1_500, x: 270, y: 300, hide: false },
    { at: 2_100, x: DEMO_A.x, y: DEMO_A.y },
    { at: 2_400, x: DEMO_A.x, y: DEMO_A.y, down: true },
    { at: 2_550, x: DEMO_A.x, y: DEMO_A.y, down: false },
    { at: 3_100, x: DEMO_B.x, y: DEMO_B.y },
    { at: 3_400, x: DEMO_B.x, y: DEMO_B.y, down: true },
    { at: 3_550, x: DEMO_B.x, y: DEMO_B.y, down: false },
    { at: 4_100, x: DEMO_B.x, y: DEMO_B.y, down: true },
    { at: 4_250, x: DEMO_B.x, y: DEMO_B.y, down: false },
    { at: 5_000, x: 270, y: 300 },
    { at: 5_200, x: 270, y: 300, hide: true },
  ],
  steps: [
    { at: 0, en: 'LOOK AT THE WHOLE PICTURE', no: 'SE PÅ HELE BILDET' },
    { at: 1_600, en: 'TAP A TILE TO TURN IT', no: 'TRYKK PÅ EN BIT FOR Å SNU' },
    { at: 3_300, en: 'TURN EACH ONE RIGHT WAY UP', no: 'SNU HVER BIT RIKTIG VEI' },
  ],
};

export const game = thirtyGame<MendView>({
  Board,
  View,
  demo,
  mistakes: false,
  words: {
    en: {
      instruction: 'Tap a tile to turn it. Turn them all until the picture is whole.',
      howMove: 'Tap a tile to turn it a quarter. Turn each one until it fits.',
    },
    no: {
      instruction: 'Trykk på en bit for å snu den. Snu dem til bildet er helt.',
      howMove: 'Trykk på en bit for å snu den en kvart runde. Snu hver til den passer.',
    },
  },
});
